import {
  PreScreeningQuestion,
  QuestionType,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  MEASUREMENT_ATTRIBUTES,
  CARDIOVASCULAR_ATTRIBUTES,
  CANCER_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for Cidara Flu Vaccine Study
 * Target population: Healthy adults 65+ with weight >40kg and BMI >18 kg/m² interested in influenza vaccine clinical trial
 */
export const cidaraFluVaxScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "age_eligibility",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "17_or_under",
        label: "17 or under",
        disqualify: true
      },
      {
        value: "18_to_49",
        label: "18 to 49",
        disqualify: true
      },
      {
        value: "50_to_54",
        label: "50 to 54",
        disqualify: true
      },
      {
        value: "65_or_older",
        label: "65 or older"
      },
    ],
    disqualifyMessage: "This study is for adults aged 65 and older.",
    attributeMapping: mappers.value({
      "17_or_under": [],
      "18_to_49": [],
      "50_to_54": [],
      "65_or_older": [],
    }),
  },

  {
    id: "recent_influenza_diagnosis",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with influenza by a medical professional within the past 6 months?",
    helpText: "This refers to a confirmed diagnosis of influenza (flu) by a doctor or healthcare provider in the past 6 months (since April 2025).",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have been diagnosed with influenza within the past 6 months",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not been diagnosed with influenza within the past 6 months"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have been diagnosed with influenza in the past 6 months cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 23501, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: 23501, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "exclusionary_conditions",
    type: QuestionType.CHECKBOX,
    question: "Are any of the following true for you?",
    helpText: "Please select all that apply. If none of these apply to you, select 'None of the above'.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "pregnant_breastfeeding",
        label: "I am pregnant or breastfeeding",
        disqualify: true
      },
      {
        value: "major_surgery",
        label: "I have had major surgery in the past month or am scheduled to undergo major surgery in the next 6 months",
        disqualify: true
      },
      {
        value: "kidney_failure_dialysis",
        label: "I am on dialysis for end stage kidney disease",
        disqualify: true
      },
      {
        value: "bleeding_disorder",
        label: "I have a bleeding disorder",
        disqualify: true
      },
      {
        value: "blood_donation",
        label: "I have donated blood within the past month",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above"
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants with these conditions or circumstances.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        pregnant_breastfeeding: [
          { attributeDefinitionId: 26453, booleanAttributeValue: true }, // is_pregnant_prerequisite
          { attributeDefinitionId: 26455, booleanAttributeValue: true }, // is_breastfeeding_prerequisite
        ],
        major_surgery: [
          { attributeDefinitionId: 15603, booleanAttributeValue: true }, // had_majorsurgery_1month
          { attributeDefinitionId: 21967, booleanAttributeValue: true }, // majorsurgery_planned_6_months
        ],
        kidney_failure_dialysis: [
          { attributeDefinitionId: 20254, booleanAttributeValue: true }, // has_endstage_kidney_disease
          { attributeDefinitionId: 23565, booleanAttributeValue: true }, // has_dialysis_current
        ],
        bleeding_disorder: [
          { attributeDefinitionId: 15061, booleanAttributeValue: true }, // has_bleedingdisorder
        ],
        blood_donation: [
          { attributeDefinitionId: 23728, booleanAttributeValue: true }, // has_donated_blood_1_month
        ],
        none: [],
      },
      noneOfAboveValue: "none",
    }),
  },

  {
    id: "medical_exclusionary_conditions",
    type: QuestionType.CHECKBOX,
    question: "Are any of the following true for you?",
    helpText: "Please select all that apply. If none of these apply to you, select 'None of the above'.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "lung_condition",
        label: "I have a lung condition such as COPD, moderate to severe asthma, or bronchiectasis",
        disqualify: true
      },
      {
        value: "heart_disease",
        label: "I have heart disease, such as a history of a heart attack, heart failure or congenital heart disease",
        disqualify: true
      },
      {
        value: "takes_insulin",
        label: "I take insulin for diabetes",
        disqualify: true
      },
      {
        value: "chronic_kidney_disease",
        label: "I have chronic kidney disease",
        disqualify: true
      },
      {
        value: "blood_cancer_recent",
        label: "I have been diagnosed with a blood cancer such as leukaemia in the past 5 years and have had treatment in the past 1 year",
        disqualify: true
      },
      {
        value: "cancer_recent_treatment",
        label: "I have had another type of cancer in the past 1 year and received chemotherapy or immunotherapy",
        disqualify: true
      },
      {
        value: "transplant_history",
        label: "I have had an organ or stem cell transplant in the past",
        disqualify: true
      },
      {
        value: "immunosuppressant_medications",
        label: "I take immunosuppressing medications (such as prednisone, methotrexate, azathioprine, cyclosporine, mycophenolate, or rituximab)",
        disqualify: true
      },
      {
        value: "immune_deficiency",
        label: "I have a diagnosis of immune deficiency",
        disqualify: true
      },
      {
        value: "hiv_advanced",
        label: "I have advanced or untreated HIV",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above is true for me"
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants with these medical conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        lung_condition: [
          { attributeDefinitionId: 23497, booleanAttributeValue: true }, // has_chronic_lung_disease
        ],
        heart_disease: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: true },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: true },
          { attributeDefinitionId: 3436, booleanAttributeValue: true }, // has_coronaryarterydisease (general heart disease)
        ],
        takes_insulin: [
          { attributeDefinitionId: 23574, booleanAttributeValue: true }, // has_insulin_daily
        ],
        chronic_kidney_disease: [
          { attributeDefinitionId: 794, booleanAttributeValue: true }, // has_kidneydisease
        ],
        blood_cancer_recent: [
          { attributeDefinitionId: 23423, booleanAttributeValue: true }, // has_haematological_malignancy (blood cancer)
        ],
        cancer_recent_treatment: [
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: true },
          { attributeDefinitionId: 15527, booleanAttributeValue: true }, // has_current_cancer_treatment (chemotherapy/immunotherapy)
        ],
        transplant_history: [
          { attributeDefinitionId: 15039, booleanAttributeValue: true }, // had_transplant (organ or stem cell)
        ],
        immunosuppressant_medications: [
          { attributeDefinitionId: 3052, booleanAttributeValue: true }, // is_immunocompromised
        ],
        immune_deficiency: [
          { attributeDefinitionId: 3052, booleanAttributeValue: true }, // is_immunocompromised
        ],
        hiv_advanced: [
          { attributeDefinitionId: 23426, booleanAttributeValue: true }, // has_HIV_advanced
        ],
        none: [],
      },
      noneOfAboveValue: "none",
    }),
  },

  {
    ...standardHeightQuestion,
    attributeMapping: mappers.height({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT }),
  },
  {
    ...standardWeightQuestion,
    minWeight: 89, // 40kg = 88.18 lbs, rounded up to 89
    minBMI: 18,
    attributeMapping: mappers.weight({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT }),
  },

  {
    id: "concurrent_trial",
    type: QuestionType.RADIO,
    question: "Are you currently enrolling in or participating in another clinical trial?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I am not currently enrolling in or participating in another clinical trial"
      },
      {
        value: "yes_recent_medication",
        label: "Yes, I am currently participating in another clinical trial and I have received clinical trial medication in the past 3 months",
        disqualify: true
      },
      {
        value: "yes_follow_up_only",
        label: "Yes, I am participating in the follow-up of a clinical trial but haven't received any trial medication in the past 3 months"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have received clinical trial medication in the past 3 months cannot participate in this study.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
      yes_recent_medication: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
      yes_follow_up_only: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
    }),
  },

];
